import { z } from 'zod';
/**
 * IANA timezone enum for entities (Facility, Recipient) whose
 * local-time semantics matter for downstream logic — primarily
 * Phase 8+ rate-engine shift-time-bucket computation + Phase 10+
 * booking scheduling.
 *
 * v1 scope is Ontario (Q4 single-province). Ontario is USUALLY described as
 * spanning two timezones — Eastern for most of it, Central for the corner west
 * of 90°W — but that description needs THREE IANA zones to be correct, because
 * one pocket of the province does not observe DST at all:
 *
 *   America/Toronto    Eastern, DST observed. Most of Ontario, incl. Thunder Bay.
 *   America/Winnipeg   Central, DST observed. Kenora, Fort Frances, Rainy River.
 *   America/Atikokan   EST year-round, NO DST. Atikokan, Pickle Lake.
 *
 * `America/Atikokan` is NOT "the Central zone" — it is a fixed UTC−5 island:
 *
 *              winter            summer
 *   Toronto    13:00 EST         14:00 EDT
 *   Atikokan   13:00 EST         13:00 EST     <- never changes
 *   Winnipeg   12:00 CST         13:00 CDT
 *
 * So Atikokan coincides with Eastern in winter and with Central in summer, and
 * matches NEITHER year-round. This enum previously carried only Toronto +
 * Atikokan, with Atikokan labelled "Central Time (Kenora area)". A Kenora
 * facility onboarded on that label would have sat an hour off every winter
 * (CST is UTC−6; Atikokan is UTC−5) — wrong shift instants, wrong night-premium
 * buckets, wrong stat-holiday calendar dates, wrong cycle boundaries, for
 * roughly November through March. Latent rather than live (no facility was on a
 * non-Toronto zone), and corrected here before any row was written.
 *
 * Expansion to the rest of Canada (Atlantic, Newfoundland, Mountain, Pacific)
 * is tracked in the multi-jurisdiction-readiness memory — one-line enum
 * addition when v1.x/v2 opens new provinces.
 *
 * KNOWN GAP (docs/security/known-gaps.md, "Timezone correctness"): the values
 * here are now right, but the FRONTENDS do not yet honour them — every portal /
 * admin time input and display works in the browser's zone, and shift + offer
 * payloads carry no timezone at all. Harmless while every entity is Toronto and
 * every user is in Eastern; must be closed before onboarding any facility on
 * Winnipeg or Atikokan.
 *
 * Why a Zod enum, not free-form text:
 *   - API rejects invalid values at the validation layer
 *   - Surfaces in the OpenAPI spec as an enum → Swagger UI shows a
 *     dropdown automatically (handy until Phase 14 ships the admin UI)
 *   - Frontends render dropdowns from the same value list with no
 *     drift risk
 *   - TypeScript-typed everywhere via z.infer
 */
export const IANATimezoneSchema = z.enum([
    'America/Toronto',
    'America/Winnipeg',
    'America/Atikokan',
]);
/** Friendly UI labels for the dropdown — keyed by the IANA value.
 *  Frontends import this to render their dropdown options.
 *
 *  Led by PLACE NAMES, because the operator picking one is matching a facility
 *  address, not reasoning about UTC offsets — and the Atikokan/Winnipeg
 *  distinction is invisible from the address alone half the year. */
export const IANA_TIMEZONE_LABELS = {
    'America/Toronto': 'Eastern Time — most of Ontario (Toronto, Ottawa, Thunder Bay)',
    'America/Winnipeg': 'Central Time — Kenora, Fort Frances, Rainy River',
    'America/Atikokan': 'EST year-round, no daylight saving — Atikokan, Pickle Lake',
};
//# sourceMappingURL=timezone.schema.js.map